import { v4 as uuidv4 } from 'uuid';
import eventManager from "../../store/sqftEventManager";
import config from "../../store/sqftConfig";
import workspace from "../workspace";
import store from "../../../../../../store";
import {setCanvasToolReady} from "../../../../../../actions/sqftResourceFunctions";
import {updateSQFTCalculations} from "../../../../../../actions/sqftFunctions";
import {setSQFTMenuOpened} from "../../../../../../actions/sqftMenuFunctions";
import {attachResource, attachResourceWithConfig} from "./resourseCanas";
import {getStringFeatureName} from "../../../../../../misc/helpers";
import MiteredController from "./mitered";
import {get3dURLsForFeature} from "../../../../helpers/sqft3dArHelper";
import CanvasCursor from "./CanvasCursor";
import {
  RESOURCE_ATTACHED_TO_TYPE,
  SIDEBAR_NAVIGATION_BUTTONS,
  SIDEBAR_NAVIGATION_CABINET_BUTTONS,
  SIDEBAR_HIDDEN_BUTTONS,
  SQFT_FEATURES,
  SQFT_GROUP
} from "../../../../constants";
import {getShapeFeatureId} from "../../../../helpers/featureHelper";
import {getBacksplashFeatureGroup, getCountertopPartNames} from "../../../../helpers/shapeHelper";
import CountertopSwitchers from "../../../../../kvComponent/navigation/countertopSwitchers";
import {getCurrentLayout} from "../../../../../appLayout";

class UniversalCanvasManager {
  constructor() {
    this.currentTool = null;
    this.currentFeatureId = null;
    this.cursor = CanvasCursor;
    this.isDestroyed = false;
  }

  init() {
    this.isDestroyed = false;
  }

  shutdown() {
    this.deactivateToolOnly();
    this.isDestroyed = true;
  }

  destroy() {
    // Only deactivate tool, keep manager alive (called by cursor manager)
    this.deactivateToolOnly();
  }

  deactivateOnly(protect, revertBackAfterMouseUp) {
    void protect;
    void revertBackAfterMouseUp;
    this.deactivateToolOnly();
  }

  // Direct method to activate tool - called from actions
  activateToolDirect(toolType, feature, settings = {}) {
    if (this.isDestroyed) return;

    const newFeatureId = this.getFeatureId(feature);

    // Always activate - even if same tool/feature (for reactivation)
    this.currentTool = toolType;
    this.currentFeatureId = newFeatureId;

    try {
      this.setHighlighting(toolType, feature, settings.disallowDemo);
      this.setupCursor(toolType, feature);
      this.registerEvents();

      this.cursor.updateCursor();
      store.dispatch(setCanvasToolReady());
    } catch (error) {
      store.dispatch(setCanvasToolReady());
    }
  }

  // Direct method to deactivate - called from actions
  deactivateToolDirect() {
    this.deactivateToolOnly();
  }

  getFeatureId(feature) {
    if (!feature) return null;
    if (feature.dbFeature && feature.dbFeature.id) return feature.dbFeature.id;
    if (feature.size) return feature.size;
    if (feature.id) return feature.id;
    return JSON.stringify(feature);
  }

  deactivateToolOnly() {
    eventManager.resetCursor(this);
    eventManager.removeEvent(this);
    config.highlight.lines = false;
    config.highlight.linesWithShapePartIds = [];
    this.cursor.clearCursor();
    this.currentTool = null;
    this.currentFeatureId = null;
  }

  setHighlighting(toolType, feature, disallowDemo) {
    workspace.setAllowEdges(false);
    workspace.setAllowBumpoutEdges(false);
    config.highlight.points = false;
    config.highlight.lines = true;

    if (toolType === 'edge') {
      config.highlight.type = 'edge';
      config.highlight.id = feature.dbFeature ? feature.dbFeature.id : feature.id;
    } else if (toolType === 'backsplash') {
      config.highlight.type = feature.size === "wall" ? 'wall' : 'backsplash';
      config.highlight.id = feature.size;
    } else if (toolType === 'bumpout') {
      config.highlight.type = 'backsplash';
    } else if (toolType === 'crown_molding') {
      config.highlight.linesWithShapePartIds = [SQFT_FEATURES.upper_cabinet_boxes];
    } else if (toolType === 'end_cap') {
      config.highlight.type = RESOURCE_ATTACHED_TO_TYPE.end_cap;
    } else {
      config.highlight.type = toolType;
      config.highlight.id = feature.id || feature.size;
    }

    if (!disallowDemo) {
      config.highlight.demoShow = true;
      setTimeout(() => {
        config.highlight.demoShow = false;
      }, 1000);
    }
  }

  setupCursor(toolType, feature) {
    void toolType;
    void feature;
    eventManager.setCursor('edge-select', this, true);
  }

  registerEvents() {
    eventManager.removeEvent(this);

    eventManager.addEvent(
      eventManager.events.RENDER,
      this.renderCursor.bind(this),
      this,
      eventManager.priorities.OVERALL
    );

    eventManager.addEvent(
      eventManager.events.MOUSE_UP,
      this.onMouseUp.bind(this),
      this,
      eventManager.priorities.OVERALL
    );

    eventManager.addEvent(
      eventManager.events.MOUSE_MOVE,
      this.onMouseMove.bind(this),
      this,
      eventManager.priorities.OVERALL
    );
  }

  onMouseMove(mousePos) {
    this.cursor.updatePosition(mousePos);
  }

  onMouseUp(mousePos) {
    void mousePos;
    const state = store.getState();
    const resourceState = state.sqftResourceState;

    if (!resourceState.toolReady || !resourceState.isToolActive) {
      return;
    }

    const result = this.getMouseTarget(resourceState.activeTool);
    if (!result.hasTarget) return;

    if (result.canApply) {
      this.applyTool(
        result.target,
        result.targetCorner,
        resourceState.activeTool,
        resourceState.currentFeature,
        resourceState.toolSettings
      );

      const updateCalculations = state.sqftState.updateCalculations;
      store.dispatch(updateSQFTCalculations(updateCalculations + 1));
    } else {
      this.handleInvalidTarget(resourceState.activeTool);
    }
  }

  getMouseTarget(toolType) {
    const hoverShape = workspace.getHoveredSqftShape();

    if (toolType === 'edge' || toolType === 'backsplash') {
      if (hoverShape && hoverShape.getHoverSegment) {
        const segCorner = hoverShape.getHoverSegmentOutlineCorner();
        const seg = hoverShape.getHoverSegment();
        return {
          hasTarget: true,
          canApply: !!(seg || segCorner),
          target: seg,
          targetCorner: segCorner
        };
      }
    } else if (toolType === 'bumpout') {
      if (hoverShape && hoverShape.getHoverSegment) {
        const seg = hoverShape.getHoverSegment();
        return {
          hasTarget: true,
          canApply: seg && (!seg.isWithBumpout() || !seg.isWithMitered()),
          target: seg,
          targetCorner: null
        };
      }
    } else if (toolType === 'crown_molding') {
      const hoveredLine = workspace.getHoveredLine();
      const hoveredLineCorner = workspace.getHoveredLineCorner();
      return {
        hasTarget: true,
        canApply: hoveredLine && this.canAttachCrownMolding(hoveredLine),
        target: hoveredLine,
        targetCorner: hoveredLineCorner
      };
    } else if (toolType === 'end_cap') {
      const hoveredLineEndCap = workspace.getHoveredLine();
      const hoveredLineCornerEndCap = workspace.getHoveredLineCorner();
      return {
        hasTarget: true,
        canApply: !!hoveredLineEndCap,
        target: hoveredLineEndCap,
        targetCorner: hoveredLineCornerEndCap
      };
    }

    return { hasTarget: false, canApply: false };
  }

  canAttachCrownMolding(line) {
    const shape = line.getParent();
    return shape && shape.partId === SQFT_FEATURES.upper_cabinet_boxes;
  }

  applyTool(target, targetCorner, toolType, feature, settings) {
    if (toolType === 'edge') {
      this.applyEdgeTool(target, targetCorner, feature, settings);
    } else if (toolType === 'backsplash') {
      this.applyBacksplashTool(target, targetCorner, feature);
    } else if (toolType === 'bumpout') {
      this.applyBumpoutTool(target);
    } else if (toolType === 'crown_molding') {
      this.applyCrownMoldingTool(target, targetCorner, feature);
    } else if (toolType === 'end_cap') {
      this.applyEndCapTool(target, targetCorner, feature);
    }
  }

  applyEdgeTool(seg, segCorner, edgeFeature, settings) {
    const state = store.getState();
    const fabricatorData = state.fabricatorState;
    const intl = workspace.intl;

    if (seg && seg.isWithMitered()) {
      seg.deleteMitered();
    } else if (seg && (seg.isWithWaterfallToolEdge() || seg.isWithPeninsulaUnderwrapEdge())) {
      seg.deleteAttachedItem();
    }

    attachResourceWithConfig({
      seg,
      segCorner,
      resourceType: 'edge',
      featureId: edgeFeature.dbFeature ? edgeFeature.dbFeature.id : edgeFeature.id,
      partId: edgeFeature.partId,
      featureName: edgeFeature.getFeatureName ? edgeFeature.getFeatureName() :
        getStringFeatureName(edgeFeature, intl.messages, intl.locale, fabricatorData),
      newWizardGroupId: settings.newWizardGroupId,
    });

    this.handleSpecialEdgeTypes(seg, edgeFeature, intl, fabricatorData);
  }

  handleSpecialEdgeTypes(seg, edgeFeature, intl, fabricatorData) {
    const edgeName = edgeFeature.getFeatureName ?
      edgeFeature.getFeatureName() :
      getStringFeatureName(edgeFeature, intl.messages, intl.locale, fabricatorData);

    const lowerCaseEdgeName = edgeName.toLowerCase();

    if (lowerCaseEdgeName.includes("peninsula underwrap")) {
      if (seg && !seg.isWithPeninsulaUnderwrapEdge()) {
        seg.addPeninsulaUnderwrapToLine();
        store.dispatch(setSQFTMenuOpened(SIDEBAR_HIDDEN_BUTTONS.toolWaterfall));
      }
    }

    if (lowerCaseEdgeName.includes("miter underwrap")) {
      if (workspace.miteredController === null) {
        workspace.miteredController = new MiteredController();
      }
      workspace.miteredController.attachMiteredUnderwrapEdge(seg);
    }
  }

  applyBacksplashTool(seg, segCorner, backSplashFeature) {
    const resourceType = (backSplashFeature.size === "wall") ? "wall" : "backsplash";

    if (seg && seg.isWithMitered()) {
      seg.deleteMitered();
    } else if (seg && (seg.isWithWaterfallToolEdge() || seg.isWithPeninsulaUnderwrapEdge())) {
      seg.deleteAttachedItem();
    }

    //get backsplash feture from countertops feature
    const layout = getCurrentLayout();
    const backsplashFeatureGroup = getBacksplashFeatureGroup();
    const [countertopPartName] = getCountertopPartNames();
    const shapesFeatureId = getShapeFeatureId(countertopPartName);
    const shapesFeature = layout.getFeatureById(shapesFeatureId)

    const neededFeature = CountertopSwitchers.getNeededFeatureFromNavItems(shapesFeature, backsplashFeatureGroup, false);

    attachResourceWithConfig({
      seg,
      segCorner,
      resourceType,
      featureId: neededFeature?.part?.dbFeature?.id,
      partId: neededFeature?.part?.partId,
      featureName: backSplashFeature.name,
      //resourceId: backSplashFeature.size,
      height: backSplashFeature.size,
    })
  }

  applyBumpoutTool(seg) {
    attachResource(seg, null, RESOURCE_ATTACHED_TO_TYPE.bumpout, null, null, '', 0, uuidv4());
  }

  applyCrownMoldingTool(hoveredLine, hoveredLineCorner, feature) {
    const state = store.getState();
    const selectedFeature = state.sqftResourceState.selectedFeature || feature;
    const featureNameTranslated = selectedFeature.getFeatureName();

    attachResource(
      hoveredLine,
      hoveredLineCorner,
      RESOURCE_ATTACHED_TO_TYPE.crown_molding,
      selectedFeature.dbFeature.id,
      SQFT_FEATURES.crown_molding,
      featureNameTranslated
    );

    const URLByFeatureName = get3dURLsForFeature(selectedFeature);
    hoveredLine.setCrownMolding3dURL(URLByFeatureName);
    hoveredLine.setCrownMoldingFeatureId(selectedFeature.dbFeature.id);
  }

  applyEndCapTool(hoveredLine, hoveredLineCorner, feature) {
    const state = store.getState();
    const selectedFeature = state.sqftResourceState.selectedFeature || feature;
    const featureNameTranslated = selectedFeature.getFeatureName();

    attachResource(
      hoveredLine,
      hoveredLineCorner,
      RESOURCE_ATTACHED_TO_TYPE.end_cap,
      selectedFeature.dbFeature.id,
      selectedFeature.partId,
      featureNameTranslated
    );
  }

  handleInvalidTarget(toolType) {
    const state = store.getState();
    const SQFTGroup = state.sqftMenuState.SQFTGroup;

    if (toolType === 'backsplash') {
      store.dispatch(setSQFTMenuOpened('backsplash'));
    } else if (toolType === 'crown_molding') {
      store.dispatch(setSQFTMenuOpened(SIDEBAR_NAVIGATION_CABINET_BUTTONS.crown_molding));
    } else if (toolType === 'end_cap') {
      const button = SQFTGroup === SQFT_GROUP.cabinets ?
        SIDEBAR_NAVIGATION_CABINET_BUTTONS.end_panel :
        SIDEBAR_NAVIGATION_BUTTONS.end_cap;
      store.dispatch(setSQFTMenuOpened(button));
    }
  }

  renderCursor(ctx) {
    this.cursor.render(ctx);
  }
}

export default new UniversalCanvasManager();
